import {
  EMAIL_SESSION_CLAIM,
  BlockingFunctionRejectionCodes,
  NeoTaxEnvironment,
  ProvisioningErrorCodes,
  assertNever,
} from '@common/neo.tax';
import { captureException } from '@sentry/react';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import z from 'zod';

import { AuthErrorType, LocalStorageKey } from '@neo.tax/shared/enums';
import { ClientReportableLoginMethod } from '@neo.tax/shared/graphql/types';
import { env } from '@neo.tax/shared/utils';
import { toHttpUrl } from '@neo.tax/shared/utils/url.utils';

import { redirectToGoogleOAuth } from '../google';
import { redirectToMicrosoftOAuth } from '../microsoft';
import { AuthenticationUser, MaybeFirebaseError } from './firebase.types';
import { EnterpriseSsoProvider, SsoProvider } from './sso-provider.enum';
import { VPCSC_OUTCOME_TAG, VpcscRefusalOutcome, isVpcscCheckFailedError } from './vpcsc.utils';

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string;
  }
}

const INVALID_PASSWORD_ERROR = 'Invalid username and/or password.';
const SOMETHING_WENT_WRONG_ERROR = 'Something went wrong. Please try again.';
export const SERVICE_BUSY_ERROR =
  'Our system is taking longer than usual to respond. Please wait a moment and try again.';
// Deliberately offers no retry: the account cannot sign in until its identity provider is configured to
// release an email address, so the only way forward is someone changing that.
export const NO_PROVIDER_EMAIL_ERROR =
  'Your organization’s login provider did not share an email address with us, which is required to sign in. Please contact your IT administrator or Neo.Tax support.';

/** How Identity Platform prefixes an `auth/internal-error` raised by the sign-in blocking function. */
const BLOCKING_FUNCTION_ERROR_MARKER = 'BLOCKING_FUNCTION_ERROR_RESPONSE';

/** Ways a user can abandon the sign-in popup themselves, none of which are a fault worth recording. */
const POPUP_DISMISSAL_CODES = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request']);

/**
 * Recorded on the login audit row when Identity Platform refuses to attach a provider to an address that
 * already has an account. States the cause, not the remedy, which differs by provider.
 */
const ACCOUNT_EXISTS_REJECTION_REASON = 'Account exists with a different sign-in method';

const VPCSC_REJECTION_REASON = 'Refused by VPC Service Controls';

const UNDELETED_ACCOUNT_REJECTION_REASON =
  'Refused sign-up left an account it could not delete; delete it under Customer Success → Firebase Identity Search';

/**
 * How each provider is named on a login-audit row.
 *
 * Written out rather than derived from the provider name, though several pairs match: the two vocabularies are
 * independent, and a `Record` keyed on the enum makes adding a provider fail to compile here rather than
 * record sign-ins under a method chosen by string coincidence.
 */
const SSO_PROVIDER_AUDIT_METHODS: Record<SsoProvider, ClientReportableLoginMethod> = {
  [SsoProvider.GOOGLE]: ClientReportableLoginMethod.GoogleSso,
  [SsoProvider.MICROSOFT]: ClientReportableLoginMethod.MicrosoftSso,
  [SsoProvider.OIDC]: ClientReportableLoginMethod.Oidc,
  [SsoProvider.SAML]: ClientReportableLoginMethod.Saml,
};

const FIREBASE_AUTH_EMULATOR_HOST = '127.0.0.1:9099';

const firebaseConfig = {
  apiKey: env.REACT_APP_FIREBASE_API_KEY,
  authDomain: `${env.REACT_APP_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: env.REACT_APP_FIREBASE_APP_ID,
};

const googleOAuthProvider = new firebase.auth.GoogleAuthProvider();
googleOAuthProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
googleOAuthProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');

const microsoftOAuthProvider = new firebase.auth.OAuthProvider('microsoft.com');
// custom OAuth parameters: https://docs.microsoft.com/en-us/azure/active-directory/develop/v1-protocols-oauth-code
microsoftOAuthProvider.setCustomParameters({ prompt: 'consent' }); // force re-consent

const getAuthProvider = (
  ssoProvider: SsoProvider,
  enterpriseSsoProviderId?: string,
): firebase.auth.GoogleAuthProvider | firebase.auth.OAuthProvider | firebase.auth.SAMLAuthProvider => {
  switch (ssoProvider) {
    case SsoProvider.GOOGLE:
      return googleOAuthProvider;
    case SsoProvider.MICROSOFT:
      return microsoftOAuthProvider;
    case SsoProvider.OIDC:
      if (!enterpriseSsoProviderId) {
        throw new Error('Provider ID is required when using OIDC');
      }
      return new firebase.auth.OAuthProvider(enterpriseSsoProviderId);
    case SsoProvider.SAML:
      if (!enterpriseSsoProviderId) {
        throw new Error('Provider ID is required when using SAML');
      }
      return new firebase.auth.SAMLAuthProvider(enterpriseSsoProviderId);
    default:
      throw new Error(`Provider ${ssoProvider} is not supported`);
  }
};

/**
 * Identity Platform's public endpoint for starting a federated sign-in, which a popup sign-in calls with the
 * same web API key to find the provider's login page.
 */
const CREATE_AUTH_URI_URL = 'https://identitytoolkit.googleapis.com/v1/accounts:createAuthUri';

const ENTERPRISE_SSO_PROVIDERS: readonly EnterpriseSsoProvider[] = [SsoProvider.OIDC, SsoProvider.SAML];

const CreateAuthUriFoundSchema = z.object({ authUri: z.string() });
const CreateAuthUriNotFoundSchema = z.object({
  error: z.object({ message: z.string().startsWith('OPERATION_NOT_ALLOWED') }),
});

/** Whether Identity Platform has a provider by this ID, or null when its answer was neither yes nor no. */
const isEnterpriseSsoProviderConfigured = async (providerId: string): Promise<boolean | null> => {
  const response = await fetch(`${CREATE_AUTH_URI_URL}?key=${encodeURIComponent(firebaseConfig.apiKey)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ providerId, continueUri: `https://${firebaseConfig.authDomain}/__/auth/handler` }),
  });
  const body: unknown = await response.json();
  if (response.ok && CreateAuthUriFoundSchema.safeParse(body).success) {
    return true;
  }
  if (CreateAuthUriNotFoundSchema.safeParse(body).success) {
    return false;
  }
  return null;
};

/**
 * Asks this environment's Identity Platform which enterprise provider types exist for a provider ID suffix.
 *
 * Asks only about the two IDs built from the suffix, through the same call a sign-in with either would make, so it
 * learns nothing about a provider that attempting to sign in with it would not. Null when either answer could not
 * be read, leaving the sign-in to go ahead and fail on its own terms.
 */
export const lookUpEnterpriseSsoProviderTypes = async (
  providerIdSuffix: string,
): Promise<ReadonlySet<EnterpriseSsoProvider> | null> => {
  // The Auth emulator is served from its own host, which this request does not go to.
  if (env.REACT_APP_USE_FIREBASE_EMULATOR === true) {
    return null;
  }
  try {
    const answers = await Promise.all(
      ENTERPRISE_SSO_PROVIDERS.map(async (type) => ({
        type,
        configured: await isEnterpriseSsoProviderConfigured(`${type.toLowerCase()}.${providerIdSuffix}`),
      })),
    );
    if (answers.some(({ configured }) => configured === null)) {
      return null;
    }
    return new Set(answers.filter(({ configured }) => configured).map(({ type }) => type));
  } catch {
    return null;
  }
};

/** How long a failed sign-in waits on Identity Platform to say whether the provider ID exists before giving up. */
const PROVIDER_ID_CHECK_TIMEOUT_MS = 5000;

/**
 * Whether Identity Platform answers that it has no provider by this ID. False whenever that could not be
 * established, including when no answer arrives within PROVIDER_ID_CHECK_TIMEOUT_MS, so a failure with some other
 * cause keeps its own advice and the sign-in it belongs to still ends.
 */
const isEnterpriseSsoProviderIdUnknown = async (providerId: string | undefined): Promise<boolean> => {
  if (!providerId || env.REACT_APP_USE_FIREBASE_EMULATOR === true) {
    return false;
  }
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => resolve(null), PROVIDER_ID_CHECK_TIMEOUT_MS);
  });
  try {
    return (await Promise.race([isEnterpriseSsoProviderConfigured(providerId), timedOut])) === false;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const initializeAuthenticationService = (): void => {
  const firebaseApp = firebase.initializeApp(firebaseConfig);

  if (env.REACT_APP_USE_FIREBASE_EMULATOR === true) {
    firebase.auth().useEmulator(`http://${FIREBASE_AUTH_EMULATOR_HOST}`);
    // Skip App Check entirely when using the Auth emulator. The emulator does
    // not enforce App Check, and initializeAppCheck would attempt a real
    // network call to the reCAPTCHA Enterprise backend.
    return;
  }

  if (env.REACT_APP_NEO_TAX_ENV === NeoTaxEnvironment.LOCAL) {
    // Real-Firebase local dev: print a debug token on first call to
    // initializeAppCheck. Engineers register the printed UUID in
    // Firebase Console → App Check → Apps → "Manage debug tokens".
    // Must be set before initializeAppCheck() is invoked.
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  try {
    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaEnterpriseProvider(env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    // Don't take auth down if App Check init fails (especially during the
    // unenforced rollout window). Surface to Sentry so we notice.
    captureException(error, { extra: { source: 'initializeAppCheck' } });
  }
};

export type EmailPasswordSignInFailure = {
  /**
   * Whether Firebase rejected the credentials themselves, as opposed to the attempt failing for a
   * reason the password had nothing to do with. Callers need the distinction because the login form
   * has richer copy for a bad credential — it points magic-link users at their link — which is actively
   * misleading advice when the password was never in question.
   */
  invalidCredentials: boolean;
  message: string;
};

export const signInWithEmailAndPassword = async (
  email: string,
  password: string,
): Promise<EmailPasswordSignInFailure | undefined> => {
  try {
    await firebase.auth().signInWithEmailAndPassword(email, password);
  } catch (error) {
    const firebaseError = error as firebase.FirebaseError;
    if (
      firebaseError.code &&
      (firebaseError.code === 'auth/wrong-password' || firebaseError.code === 'auth/invalid-credential')
    ) {
      return { invalidCredentials: true, message: INVALID_PASSWORD_ERROR };
    }
    captureException(firebaseError, { extra: { source: 'signInWithEmailAndPassword' } });
    // Email/password sign-in reaches the sign-in blocking function too, so it inherits that function's
    // failure modes even though it needs nothing from it. "Something went wrong" reads as a rejected
    // credential here, sending someone to reset a password that was never the problem.
    if (firebaseError.code === 'auth/internal-error') {
      return { invalidCredentials: false, message: SERVICE_BUSY_ERROR };
    }
    return { invalidCredentials: false, message: SOMETHING_WENT_WRONG_ERROR };
  }
};

export const EMAIL_NOT_VERIFIED_REJECTION_REASON =
  'Signed in but their email was not verified, so they saw the verify-your-email page';

/**
 * Whether there is a signed-in user whose address is unverified. False when nobody is signed in, since there
 * is then no address to be unverified.
 */
export const hasUnverifiedSignedInUser = (): boolean => firebase.auth().currentUser?.emailVerified === false;

export const signInWithCustomToken = async (customToken: string): Promise<void> => {
  await firebase.auth().signInWithCustomToken(customToken);

  // The #neo-events "Sign in" notification for these SSO flows is emitted server-side at the OAuth
  // callback that mints the custom token; the client only clears the auth-attempt markers it set before
  // redirecting.
  localStorage.removeItem(LocalStorageKey.SESSION_GOOGLE_AUTH_ATTEMPT);
  localStorage.removeItem(LocalStorageKey.SESSION_INTUIT_AUTH_ATTEMPT);
};

export const onAuthenticationStateChange = (callback: (user: AuthenticationUser | null) => void): void => {
  // According to firebase documentation...
  //
  // `OnIdTokenChanged`: Adds an observer for changes to the signed-in user's ID
  // token, which includes sign-in, sign-out, and token refresh events. This
  // method has the same behavior as firebase.auth.Auth.onAuthStateChanged had
  // prior to 4.0.0. https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth#onidtokenchanged
  //
  // `onAuthStateChanged`: Prior to 4.0.0, this triggered the observer when
  // users were signed in, signed out, or when the user's ID token changed in
  // situations such as token expiry or password change. After 4.0.0, the
  // observer is only triggered on sign-in or sign-out.
  // https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth#onauthstatechanged
  //
  // A stale token is re-minted before the user is published, so nothing reacting to `emailVerified` can
  // send a token the backend rejects. Only the latest event is published: an earlier one still waiting on
  // Firebase must not overwrite a sign-out or account switch that arrived meanwhile. The re-mint fires this
  // observer again; a user whose token still disagrees after one re-mint is published as-is. A re-mint that
  // fails fires nothing, so it is retried on that user's next event.
  let latestEvent = 0;
  let reMintedForUid: string | null = null;
  firebase.auth().onIdTokenChanged(async (user) => {
    const event = ++latestEvent;
    if (user && user.uid !== reMintedForUid && (await hasStaleEmailVerifiedToken(user).catch(() => false))) {
      reMintedForUid = user.uid;
      await user.getIdToken(/* forceRefresh */ true).catch(() => {
        if (reMintedForUid === user.uid) {
          reMintedForUid = null;
        }
      });
    }
    if (event === latestEvent) {
      callback(user);
    }
  });
};

export const isSignInWithEmailLink = (): boolean => firebase.auth().isSignInWithEmailLink(window.location.href);

export const signInWithEmailLink = async (email: string): Promise<void> => {
  await firebase.auth().signInWithEmailLink(email, window.location.href);
};

export const getAuthenticationToken = async (): Promise<string | undefined> =>
  await firebase.auth().currentUser?.getIdToken();

/**
 * Whether `user` reports a verified email that its cached ID token predates — the state of a session
 * restored after verifying elsewhere, whose token the backend's auth guard rejects with EMAIL_NOT_VERIFIED.
 * A token with no `email_verified` claim belongs to an account Firebase holds no email for, which no re-mint changes.
 */
export const hasStaleEmailVerifiedToken = async (user: AuthenticationUser): Promise<boolean> => {
  if (!user.emailVerified) {
    return false;
  }
  const { claims } = await user.getIdTokenResult();
  return claims.email_verified === false;
};

/**
 * Applies a Firebase out-of-band action code to complete an email-verification
 * flow, then force-refreshes the client's ID token so the next API call carries
 * `email_verified: true`.
 *
 * Why the refresh: `applyActionCode` updates Firebase's server-side record and
 * mutates `currentUser.emailVerified` locally, but the cached ID token (which
 * Apollo Client reads via `getIdToken()`) still carries the old claim. Without
 * a forced refresh, every subsequent authenticated request is rejected by the
 * backend's auth guard with EMAIL_NOT_VERIFIED.
 *
 * Rejects with the raw Firebase error so callers can branch on
 * `code === 'auth/expired-action-code'` / `'auth/invalid-action-code'`.
 *
 * Returns the verified email (when the action code exposes one) so the caller can pre-populate
 * the sign-in form on the post-verification redirect.
 */
export const applyEmailVerificationActionCode = async (oobCode: string): Promise<string | undefined> => {
  // Read the verified email off the action code BEFORE applying it — applyActionCode consumes the
  // oobCode, after which it can't be inspected. Returned so the post-verification redirect can
  // pre-populate the sign-in email field. checkActionCode rejects with the same
  // expired/invalid Firebase error codes as applyActionCode, so caller error handling is unchanged.
  const actionCodeInfo = await firebase.auth().checkActionCode(oobCode);
  const verifiedEmail = actionCodeInfo.data.email ?? undefined;
  await firebase.auth().applyActionCode(oobCode);
  const currentUser = firebase.auth().currentUser;
  if (currentUser) {
    // Order matters: refresh the cached ID token BEFORE `reload()` updates
    // local state. `reload()` triggers `onIdTokenChanged`, which pushes a new
    // `emailVerified: true` into the reactive store; anything listening on
    // that flag (useUserStateReconciliation) will fire a reconcile mutation
    // that reads the cached Apollo token. If `getIdToken(true)` hasn't run
    // yet, Apollo sends the stale token and the backend rejects it with
    // EMAIL_NOT_VERIFIED until a retry picks up the refreshed token.
    await currentUser.getIdToken(/* forceRefresh */ true);
    await currentUser.reload();
  }
  return verifiedEmail;
};

export const clearFirebase = (): void => {
  firebase.auth().signOut();
};

/**
 * Force-refreshes the Firebase ID token so the next authenticated API call
 * picks up any server-side user-state changes (e.g. `emailVerified` was flipped
 * since the cached token was minted). No-op when no user is signed in.
 */
export const refreshFirebaseIdToken = async (): Promise<void> => {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    return;
  }
  await currentUser.reload();
  await currentUser.getIdToken(/* forceRefresh */ true);
};

export const isMaybeFirebaseError = (error: unknown): error is MaybeFirebaseError =>
  typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string';

/** The address Identity Platform names on an `auth/account-exists-with-different-credential` error. */
const ConflictingEmailSchema = z.object({ email: z.string().trim().email() });

/**
 * Reads the address Identity Platform names on an account-conflict error.
 *
 * `auth/account-exists-with-different-credential` is the one failure that carries the address it refused,
 * because resolving it requires knowing which account to sign in with. It is also the only place this
 * attempt's address exists in the browser — the popup produced no user to read one from — so it is the only
 * way the audit row can name who was blocked.
 */
const readConflictingEmail = (error: unknown): string | undefined => {
  const parsed = ConflictingEmailSchema.safeParse(error);
  return parsed.success ? parsed.data.email.toLowerCase() : undefined;
};

/** The error codes an ExtendedGraphQLErrors response carries, which is how provisioning reports a refusal. */
const CodedGraphQlErrorSchema = z.object({
  graphQLErrors: z.array(z.object({ extensions: z.object({ errors: z.array(z.object({ code: z.string() })) }) })),
});

/**
 * Reads the reason out of a failure raised by provisioning rather than by Identity Platform.
 *
 * These arrive as a coded GraphQL error from `provisionCurrentUser`, which refuses to attach a sign-in to an
 * account that is not its own. A provider the organization does not use needs the right provider or an
 * administrator; any other identity mismatch needs the method the account was created with.
 *
 * Undefined for anything else, so a genuine fault reports as unknown rather than as an SSO problem.
 */
export const readProvisioningErrorReason = (error: unknown): AuthErrorType | undefined => {
  const parsed = CodedGraphQlErrorSchema.safeParse(error);
  if (!parsed.success) {
    return undefined;
  }
  const codes = parsed.data.graphQLErrors.flatMap(({ extensions }) => extensions.errors.map(({ code }) => code));
  if (codes.includes(ProvisioningErrorCodes.SSO_PROVIDER_MISMATCH)) {
    return AuthErrorType.SSO_PROVIDER_MISMATCH;
  }
  return codes.includes(ProvisioningErrorCodes.ACCOUNT_LINKED_TO_ANOTHER_IDENTITY)
    ? AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL
    : undefined;
};

/**
 * Which of the failures Identity Platform has collapsed into `auth/internal-error`.
 *
 * The markers are embedded by the sign-in blocking function's own rejections. Anything else is Identity
 * Platform failing on its own account, in practice the function not answering inside its deadline. Only that
 * and an unreadable account are worth retrying: the attempt that timed out is also the one that warmed the
 * instance, and an account read that failed once normally succeeds the next time.
 */
const readInternalErrorReason = (message: string | undefined): AuthErrorType => {
  // The same reason, and so the same copy, as Firebase's own refusal of this situation. Which of the two
  // rejects the sign-in turns on whether the provider asserted a verified email, which the person reading the
  // message cannot act on.
  if (message?.includes(BlockingFunctionRejectionCodes.ENTERPRISE_LINK_REFUSED)) {
    return AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL;
  }
  // The provider authenticated the user but supplied no email address. Retrying cannot change that, so
  // offering a retry would be a loop that cannot succeed.
  if (message?.includes(BlockingFunctionRejectionCodes.NO_PROVIDER_EMAIL)) {
    return AuthErrorType.NO_PROVIDER_EMAIL;
  }
  if (message?.includes(BlockingFunctionRejectionCodes.ACCOUNT_UNREADABLE)) {
    return AuthErrorType.ACCOUNT_CHECK_FAILED;
  }
  return AuthErrorType.SERVICE_BUSY;
};

/**
 * Reads which failure an `auth/internal-error` was for a magic-link sign-in, returning
 * {@link AuthErrorType.SERVICE_BUSY_LOGIN_LINK} where {@link readInternalErrorReason} returns
 * {@link AuthErrorType.SERVICE_BUSY}.
 *
 * A magic link meets the same blocking-function refusals as any other method, and those are unchanged. Only
 * the timeout differs: its copy tells the user to open the link again, which a failed attempt leaves usable.
 */
export const readEmailLinkInternalErrorReason = (message: string | undefined): AuthErrorType => {
  const reason = readInternalErrorReason(message);
  return reason === AuthErrorType.SERVICE_BUSY ? AuthErrorType.SERVICE_BUSY_LOGIN_LINK : reason;
};

export type OAuthLifecycleAdapter = {
  onStart?(): void;
  onSuccess?(): void;
  /**
   * @param reason - why the sign-in failed, when it is something the user can be told. Absent for a
   * failure with nothing to say, such as the user closing the popup themselves.
   *
   * This is how a failed sign-in is reported: {@link startOAuth} settles rather than rejecting, so a
   * caller cannot leave a user staring at a form that stopped for no visible reason simply by not
   * wrapping the call. Anything worth recording has already gone to Sentry by the time this runs.
   */
  onError?(reason?: AuthErrorType): void;
};

/**
 * What {@link startOAuth} needs from the React layer, so this util stays free of Apollo and GraphQL.
 *
 * Every callback is required. Each one is the sole mechanism behind something a caller cannot see is missing —
 * a security check, the DB user, or the only record of a failed sign-in — so an optional field would let a new
 * call site skip one silently.
 */
export type StartOAuthOptions = {
  ssoProvider: SsoProvider;
  adapter: OAuthLifecycleAdapter;
  /** Absent for Google and Microsoft; the Firebase provider id (`oidc.*` / `saml.*`) for an enterprise IdP. */
  enterpriseSsoProviderId: string | undefined;
  /**
   * Resolves the instance a single-tenant domain belongs to, via the useDomainRedirect Apollo hook. SSO cannot
   * be intercepted before the popup, so this check runs on the post-popup address.
   */
  getSingleTenantRedirectUrl: (email: string) => Promise<string | null>;
  /** Creates the DB user through the token-verified provisionCurrentUser mutation. */
  provisionCurrentUser: () => Promise<unknown>;
  /** Audits a sign-in Identity Platform refused, which reaches no backend and so leaves no other trace. */
  reportLoginFailure: (email: string, method: ClientReportableLoginMethod, rejectionReason: string) => void;
};

/**
 * Both times are reported to the whole second, and the sign-in that creates an account need not stamp both in the same
 * one. A later, separate sign-in this soon after creation is implausible, so a wide window costs nothing.
 */
const CREATING_SIGN_IN_WINDOW_MS = 60_000;

const hasNoSignInSinceCreation = ({ creationTime, lastSignInTime }: firebase.User['metadata']): boolean =>
  Date.parse(lastSignInTime ?? '') - Date.parse(creationTime ?? '') <= CREATING_SIGN_IN_WINDOW_MS;

const auditMethodOf = (user: firebase.User): ClientReportableLoginMethod => {
  const providerId = user.providerData[0]?.providerId ?? '';
  if (providerId === 'google.com') {
    return ClientReportableLoginMethod.GoogleSso;
  }
  if (providerId === 'microsoft.com') {
    return ClientReportableLoginMethod.MicrosoftSso;
  }
  if (providerId.startsWith('oidc.')) {
    return ClientReportableLoginMethod.Oidc;
  }
  return providerId.startsWith('saml.') ? ClientReportableLoginMethod.Saml : ClientReportableLoginMethod.Other;
};

/**
 * Deletes the signed-in account after provisioning refused it, if the sign-in behind this session created it. Left
 * behind, that account holds the address, and the organization's own provider can no longer sign in to it. Resolves
 * whether the account is gone, which also ends the session.
 *
 * Without the popup's credential, an account last signed in to within a minute of its creation is taken to have had no
 * sign-in but the one that created it.
 */
export const deleteRefusedNewAccount = async ({
  credential,
  reportLoginFailure,
}: {
  credential?: firebase.auth.UserCredential;
  reportLoginFailure: StartOAuthOptions['reportLoginFailure'];
}): Promise<boolean> => {
  const user = credential?.user ?? firebase.auth().currentUser;
  const createdBySessionSignIn =
    credential?.additionalUserInfo?.isNewUser ?? (user !== null && hasNoSignInSinceCreation(user.metadata));
  if (!user || !createdBySessionSignIn) {
    return false;
  }
  try {
    await user.delete();
    return true;
  } catch (error) {
    captureException(error, { extra: { source: 'deleteRefusedNewAccount' } });
    const email = await resolveSignedInEmail(user).catch(() => null);
    if (email) {
      reportLoginFailure(email, auditMethodOf(user), UNDELETED_ACCOUNT_REJECTION_REASON);
    }
    return false;
  }
};

/** What a failed {@link startOAuth} knows about the attempt, gathered for whichever handler the failure needs. */
type OAuthAttempt = Pick<
  StartOAuthOptions,
  'adapter' | 'enterpriseSsoProviderId' | 'reportLoginFailure' | 'ssoProvider'
> & {
  /** Absent when the popup itself failed. */
  credential?: firebase.auth.UserCredential;
  /** Absent until an address has been read from the completed popup. */
  signedInEmail?: string;
};

const isEnterpriseSsoProvider = (ssoProvider: SsoProvider): ssoProvider is EnterpriseSsoProvider =>
  ssoProvider === SsoProvider.OIDC || ssoProvider === SsoProvider.SAML;

const reportOAuthError = (error: unknown, { enterpriseSsoProviderId, ssoProvider }: OAuthAttempt): void => {
  captureException(error, { extra: { source: 'startOAuth', ssoProvider, enterpriseSsoProviderId } });
};

/** Reports a refusal under VPC Service Controls against the account that tried, tagged with what it did to them. */
const reportRefusal = (
  error: unknown,
  { credential, enterpriseSsoProviderId, ssoProvider }: OAuthAttempt,
  outcome: VpcscRefusalOutcome,
): void => {
  const uid = credential?.user?.uid;
  captureException(error, {
    extra: { source: 'startOAuth', ssoProvider, enterpriseSsoProviderId },
    tags: { [VPCSC_OUTCOME_TAG]: outcome },
    user: uid ? { id: uid } : undefined,
  });
};

/**
 * Whether a session can carry on after the user's network refused bringing it up to date under VPC Service
 * Controls. Every later refresh would be refused too, so it can only if the account shows a verified email and its
 * current token does not mark it unverified. A token has no `email_verified` claim at all when Firebase holds no
 * email for the account, as for a provider that publishes the address under a non-standard name.
 */
const canOutlastRefusal = async (user: firebase.User, error: unknown): Promise<boolean> =>
  isVpcscCheckFailedError(error) &&
  user.emailVerified &&
  (await user.getIdTokenResult()).claims.email_verified !== false;

/**
 * Brings the session up to date with the email provisioning just marked verified, which the token and User object
 * the popup minted predate for providers that do not assert the claim (Microsoft, SAML, some OIDC).
 *
 * The token is re-minted so authenticated API calls clear the backend's email-verified guard, and the User object is
 * reloaded so its `emailVerified` property, which a token refresh does not touch, stops sending the user to verify
 * their email. Token first, so whatever reacts to the reload's `onIdTokenChanged` reads the fresh token.
 */
const refreshSessionAfterProvisioning = async (attempt: OAuthAttempt): Promise<void> => {
  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    return;
  }
  try {
    await currentUser.getIdToken(/* forceRefresh */ true);
    await currentUser.reload();
  } catch (error) {
    if (!(await canOutlastRefusal(currentUser, error))) {
      throw error;
    }
    reportRefusal(error, attempt, VpcscRefusalOutcome.SESSION_KEPT);
  }
};

/**
 * Sends a user whose email domain belongs to a single-tenant instance to that instance's login, signing them out of
 * this one first so nothing is provisioned here.
 */
const redirectToSingleTenantInstance = async (
  singleTenantRedirectUrl: string,
  attempt: OAuthAttempt,
): Promise<void> => {
  await firebase.auth().signOut();
  // Protocol-guarded because the target is assigned to location.href, an XSS sink.
  const instanceLoginUrl = toHttpUrl('/login', singleTenantRedirectUrl);
  if (!instanceLoginUrl) {
    // Signed out with nowhere to send them. Saying so is all that is left — returning quietly would leave the form
    // spinning on an attempt that already ended.
    captureException(new Error('Single-tenant redirect target was rejected'), {
      extra: { source: 'startOAuth', ssoProvider: attempt.ssoProvider, singleTenantRedirectUrl },
    });
    attempt.adapter.onError?.(AuthErrorType.UNKNOWN);
    return;
  }
  window.location.href = instanceLoginUrl.toString();
};

export const startOAuth = async ({
  adapter,
  enterpriseSsoProviderId,
  getSingleTenantRedirectUrl,
  provisionCurrentUser,
  reportLoginFailure,
  ssoProvider,
}: StartOAuthOptions): Promise<void> => {
  const attempt: OAuthAttempt = { adapter, enterpriseSsoProviderId, reportLoginFailure, ssoProvider };
  try {
    adapter.onStart?.();

    // Inside the block: building the provider can throw for a misconfigured call, and a throw escaping
    // here would break the promise this function makes to report every failure through the adapter.
    const oauthProvider = getAuthProvider(ssoProvider, enterpriseSsoProviderId);

    attempt.credential = await firebase.auth().signInWithPopup(oauthProvider);
    attempt.signedInEmail = await resolveSignedInEmail(attempt.credential.user);

    const singleTenantRedirectUrl = await getSingleTenantRedirectUrl(attempt.signedInEmail);
    if (singleTenantRedirectUrl) {
      await redirectToSingleTenantInstance(singleTenantRedirectUrl, attempt);
      return;
    }

    // The Apollo auth link attaches the popup session's ID token, from which the server derives the email, uid,
    // and SSO provider (and enterprise provider id) — never client-supplied input.
    await provisionCurrentUser();
    await refreshSessionAfterProvisioning(attempt);

    adapter.onSuccess?.();
  } catch (error) {
    await handleOAuthFailure(error, attempt);
  }
};

const handleOAuthFailure = async (error: unknown, attempt: OAuthAttempt): Promise<void> => {
  if (isVpcscCheckFailedError(error)) {
    await handleNetworkRefusal(error, attempt);
  } else if (isMaybeFirebaseError(error)) {
    await handleFirebaseFailure(error, attempt);
  } else {
    await handleNonFirebaseFailure(error, attempt);
  }
};

/** The user's network refused a request to Identity Platform under VPC Service Controls. */
const handleNetworkRefusal = async (error: unknown, attempt: OAuthAttempt): Promise<void> => {
  reportRefusal(error, attempt, VpcscRefusalOutcome.SIGNED_OUT);
  // Signed out before the failure is reported, so nothing that reacts to the report runs on the refused session.
  try {
    await firebase.auth().signOut();
  } catch (signOutError) {
    reportOAuthError(signOutError, attempt);
  }
  if (attempt.signedInEmail) {
    attempt.reportLoginFailure(
      attempt.signedInEmail,
      SSO_PROVIDER_AUDIT_METHODS[attempt.ssoProvider],
      VPCSC_REJECTION_REASON,
    );
  }
  attempt.adapter.onError?.(AuthErrorType.VPCSC_CHECK_FAILED);
};

/** A failure Firebase did not raise: provisioning refusing the sign-in, or our own fault after the popup. */
const handleNonFirebaseFailure = async (error: unknown, attempt: OAuthAttempt): Promise<void> => {
  const provisioningReason = readProvisioningErrorReason(error);
  if (!provisioningReason) {
    // Most often our own failure to resolve an address from the completed sign-in. Naming it as unknown still beats
    // saying nothing, which strands the user on a form that stopped for no visible reason; reporting it is what
    // makes the cause findable.
    reportOAuthError(error, attempt);
    attempt.adapter.onError?.(AuthErrorType.UNKNOWN);
    return;
  }
  // Already reported and audited server-side, with the provider and the address it claimed, so it is not reported
  // again here. The popup left a live session that provisioning declined to give a user; left in place, it reads as
  // authenticated-but-unprovisioned and sends the user to finish signup, which is refused again.
  const { credential, reportLoginFailure } = attempt;
  if (!(await deleteRefusedNewAccount({ credential, reportLoginFailure }))) {
    await firebase.auth().signOut();
  }
  attempt.adapter.onError?.(provisioningReason);
};

const handleFirebaseFailure = async (error: MaybeFirebaseError, attempt: OAuthAttempt): Promise<void> => {
  // Identity Platform has no enabled provider by this ID, which for an enterprise provider means the ID typed.
  if (error.code === 'auth/operation-not-allowed' && isEnterpriseSsoProvider(attempt.ssoProvider)) {
    reportOAuthError(error, attempt);
    attempt.adapter.onError?.(AuthErrorType.ENTERPRISE_SSO_PROVIDER_REJECTED);
    return;
  }
  if (error.code === 'auth/internal-error') {
    const reason = await readPopupInternalErrorReason(error, attempt);
    // A refused enterprise link or an unreadable account is already reported from the blocking function, with the
    // evidence for it. Reporting it here too would file a second issue for one refusal.
    if (reason !== AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL && reason !== AuthErrorType.ACCOUNT_CHECK_FAILED) {
      reportOAuthError(error, attempt);
    }
    attempt.adapter.onError?.(reason);
    return;
  }
  if (error.code === 'auth/account-exists-with-different-credential') {
    handleAccountConflict(error, attempt);
    return;
  }
  // Closing the popup is the user changing their mind, not a fault — reporting it would fill Sentry with people who
  // thought better of signing in, and no message belongs on screen either.
  if (POPUP_DISMISSAL_CODES.has(error.code ?? '')) {
    attempt.adapter.onError?.();
    return;
  }
  reportOAuthError(error, attempt);
  attempt.adapter.onError?.(AuthErrorType.UNKNOWN);
};

/**
 * Which failure an `auth/internal-error` from a popup sign-in was.
 *
 * Identity Platform raises it whenever sign-in fails inside Identity Platform rather than in the browser, and relays
 * the blocking function's own message inside it, so the markers are what separate the cases. One with no blocking
 * function marker at all did not come from our backend; it is blamed on the enterprise provider ID only once Identity
 * Platform confirms it has no provider by that ID, since a timeout or a blocked network looks the same.
 */
const readPopupInternalErrorReason = async (
  error: MaybeFirebaseError,
  { enterpriseSsoProviderId, ssoProvider }: OAuthAttempt,
): Promise<AuthErrorType> => {
  const markedReason = readInternalErrorReason(error.message);
  const isUnmarkedEnterpriseFailure =
    isEnterpriseSsoProvider(ssoProvider) &&
    markedReason === AuthErrorType.SERVICE_BUSY &&
    !error.message?.includes(BLOCKING_FUNCTION_ERROR_MARKER);
  return isUnmarkedEnterpriseFailure && (await isEnterpriseSsoProviderIdUnknown(enterpriseSsoProviderId))
    ? AuthErrorType.ENTERPRISE_SSO_PROVIDER_REJECTED
    : markedReason;
};

/**
 * Identity Platform refused to attach this provider to an address that already has an account, and decided that
 * itself: no request reaches our backend, so reporting it both ways here is what makes it visible to us at all.
 *
 * Reported for every provider, not only the ones that dead-end: Google and Microsoft recover by re-signing-in and
 * linking, but the attempt still failed, and omitting them would misreport how often this is hit.
 */
const handleAccountConflict = (error: MaybeFirebaseError, attempt: OAuthAttempt): void => {
  reportOAuthError(error, attempt);
  const conflictingEmail = readConflictingEmail(error);
  if (conflictingEmail) {
    attempt.reportLoginFailure(
      conflictingEmail,
      SSO_PROVIDER_AUDIT_METHODS[attempt.ssoProvider],
      ACCOUNT_EXISTS_REJECTION_REASON,
    );
  }

  // Google and Microsoft users who signed up through the old custom-token method sign in with it again, then link
  // the real SSO provider.
  switch (attempt.ssoProvider) {
    case SsoProvider.GOOGLE:
      redirectToGoogleOAuth();
      break;
    case SsoProvider.MICROSOFT:
      redirectToMicrosoftOAuth();
      break;
    case SsoProvider.OIDC:
    case SsoProvider.SAML:
      // No redirect exists for an enterprise provider, so this is where the user is told.
      attempt.adapter.onError?.(AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL);
      break;
    default:
      attempt.adapter.onError?.(AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL);
      assertNever(attempt.ssoProvider);
  }
};

export const migrateSsoUser = async (user: firebase.User, ssoProvider: SsoProvider): Promise<void> => {
  const oauthProvider = getAuthProvider(ssoProvider);
  await user.linkWithPopup(oauthProvider);
};

/**
 * Reads the address the sign-in blocking function attached to this session.
 *
 * For a provider that publishes the address under a non-standard name, this is the only place it
 * appears client-side: Firebase cannot map such a claim onto the user record, and a blocking function
 * is not permitted to write `email` there either — so `user.email` is empty even though the provider
 * did supply an address and the backend already read it.
 */
const readSessionClaimEmail = async (user: firebase.User): Promise<string | undefined> => {
  try {
    const { claims } = await user.getIdTokenResult();
    const parsed = z.object({ [EMAIL_SESSION_CLAIM]: z.string().email() }).safeParse(claims);
    return parsed.success ? parsed.data[EMAIL_SESSION_CLAIM] : undefined;
  } catch (error) {
    // A refused network is one token error the user can act on, so it alone is passed on.
    if (isVpcscCheckFailedError(error)) {
      throw error;
    }
    // A token that cannot be read leaves nothing to fall back on, so let the caller fail on the missing
    // address rather than on a token error the user can do nothing about.
    return undefined;
  }
};

/**
 * Resolves the signed-in user's address, which is all the browser needs it for: routing a single-tenant
 * domain to its own instance before anything is provisioned.
 *
 * Interpreting an identity provider's attributes is the sign-in blocking function's job, not this one's:
 * it sees the provider's claims server-side, knows every attribute name we have encountered, and reports
 * the ones it could not use. Whatever it resolves arrives here on the session, so there are only two
 * places to look — what Firebase itself mapped, and what the blocking function attached — and no reason
 * for the browser to hold a second, narrower copy of that knowledge.
 *
 * The account's name and uid are not read here at all: provisioning derives them from the verified token
 * server-side, so a copy taken from the browser would be both redundant and less trustworthy.
 */
const resolveSignedInEmail = async (user: firebase.User | null): Promise<string> => {
  if (!user) {
    throw new Error('Missing user after Firebase SSO');
  }

  const email = user.email ?? (await readSessionClaimEmail(user));
  if (!email) {
    throw new Error('Missing email after Firebase SSO');
  }

  return email;
};
